'use client';

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import Alert from '@mui/material/Alert';
import { Spinner } from '@repo/components';
import { PERMISSIONS, InviteData, LinkData, User } from '@repo/shared/domain';
import { EventEmitter, getVerifyPermission, guardedClient as request } from '@repo/shared/helpers';
import { constructQuery } from '@repo/shared/helpers';
import UsersTab from 'src/parts/tabs/UsersTab';
import { PersonalUserDetailDrawer } from 'src/components/PersonalUserDetailDrawer';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { useConfig } from 'src/hooks/useConfig';
import { getEvent } from 'src/api/personal/events';
import { getEventUsers, getEventRoles } from 'src/api/personal/eventDetail';

interface UsersSearch {
	page?: number;
	sort?: string;
	filter?: string;
}

export const Route = createFileRoute('/personal_/events/$eventId/users')({
	validateSearch: (search: Record<string, unknown>): UsersSearch => ({
		page: (search.page as number) || undefined,
		sort: search.sort as string | undefined,
		filter: search.filter as string | undefined,
	}),
	component: UsersPage,
});

function UsersPage() {
	const { eventId } = Route.useParams();
	const { page, sort, filter } = Route.useSearch();
	const queryClient = useQueryClient();

	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();
	const { config, loading: configLoading } = useConfig();
	const defaultCountry = config?.defaultCountry || 'BE';
	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);
	const hasAllEventsView = verifyPermission([PERMISSIONS.personalAllEventsView]);
	const hasOwnEventsView = verifyPermission([PERMISSIONS.personalOwnEventsView]);

	// Fetch event to determine actual ownership
	const { data: event, isLoading: eventLoading } = useQuery({
		queryKey: ['personal-event', eventId],
		queryFn: () => getEvent(eventId, !hasAllEventsView),
	});

	// Check if user is actually the owner of the event
	const isOwner = event?.adminIds?.includes(authenticatedUser?.id || '') || false;
	const asOwner = isOwner && hasOwnEventsView;

	const canViewUsers =
		verifyPermission([PERMISSIONS.personalAllEventsUsersView]) ||
		(verifyPermission([PERMISSIONS.personalOwnEventsUsersView]) && isOwner);

	const [activeUser, setActiveUser] = useState<User | null>(null);

	const query = constructQuery({ page, sort, filter });
	const { data: usersData, isLoading: usersLoading } = useQuery({
		queryKey: ['personal-event-users', eventId, asOwner, page, sort, filter],
		queryFn: () => getEventUsers(eventId, asOwner, query),
		enabled: !!event,
	});

	const { data: roles } = useQuery({
		queryKey: ['personal-event-roles', eventId, asOwner],
		queryFn: () => getEventRoles(eventId, asOwner),
		enabled: !!event,
	});

	const basePath = asOwner
		? `/api/personal-dashboard/own-events/${eventId}`
		: `/api/personal-dashboard/events/${eventId}`;

	const revalidateEventDetail = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['personal-event', eventId] });
		queryClient.invalidateQueries({ queryKey: ['personal-event-users', eventId] });
	}, [queryClient, eventId]);

	const handleLinkUser = useCallback(
		async (linkData: LinkData) => {
			const { userId, roleIds } = linkData;
			if (!userId || event?.userIds?.includes(userId)) return;
			try {
				await request.put(`${basePath}/users`, { userId, roleIds });
				EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			} catch {
				EventEmitter.emit('toast', { type: 'error', message: 'Error linking user to event' });
			}
			revalidateEventDetail();
		},
		[basePath, revalidateEventDetail, event?.userIds],
	);

	const handleInviteUser = useCallback(
		async (data: InviteData) => {
			try {
				const { body: user } = await request.put<User>(`${basePath}/users`, data);
				EventEmitter.emit('toast', { type: 'success', message: 'User added' });
				revalidateEventDetail();
				return user;
			} catch (error: unknown) {
				const err = error as { message?: string };
				EventEmitter.emit('toast', {
					type: 'error',
					message: err?.message || 'Error adding user to event',
				});
				throw error;
			}
		},
		[basePath, revalidateEventDetail],
	);

	const handleDeleteUser = useCallback(
		async (userId: string) => {
			try {
				await request.del(`${basePath}/users/${userId}`, {});
				EventEmitter.emit('toast', { type: 'success', message: 'Changes saved' });
			} catch {
				EventEmitter.emit('toast', { type: 'error', message: 'Error' });
			}
			revalidateEventDetail();
		},
		[basePath, revalidateEventDetail],
	);

	const handleReload = () => revalidateEventDetail();

	const isLoading = authLoading || configLoading || eventLoading || usersLoading;
	if (isLoading) {
		return (
			<div className="h-full w-full flex items-center justify-center p-8">
				<Spinner />
			</div>
		);
	}

	if (!event || !authenticatedUser) return null;

	if (event?.type?.inheritUsersFromGroups) {
		return (
			<div className="p-6">
				<Alert severity="info" variant="filled">
					<strong>All Company Users Have Access</strong>
					<br />
					This event type is configured to inherit users from all company groups. All users belonging to the assigned
					companies will automatically have access to this event.
				</Alert>
			</div>
		);
	}

	return (
		<>
			<UsersTab
				onInvite={handleInviteUser}
				onLink={handleLinkUser}
				onDelete={handleDeleteUser}
				onReload={handleReload}
				event={event}
				roles={roles || []}
				authenticatedUser={authenticatedUser}
				users={usersData || { data: [], count: 0, page: 0, pageSize: 25, sort: sort || '' }}
				readOnly={!canViewUsers}
				filter={filter}
				onRowClick={({ row }) => setActiveUser(row as User)}
				permissions={{
					archive:
						(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
						verifyPermission([PERMISSIONS.personalAllEventsEdit]),
					update: false,
					create:
						(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
						verifyPermission([PERMISSIONS.personalAllEventsEdit]),
					canPrintBadges: false,
					canCreateWithoutEmail: false,
					isUserAdmin: false,
					canImport:
						(verifyPermission([PERMISSIONS.personalOwnEventsEdit]) && isOwner) ||
						verifyPermission([PERMISSIONS.personalAllEventsEdit]),
				}}
				backUrlLocalStorageKey="personalDashboardUsersBackUrl"
				defaultCountry={defaultCountry}
			/>
			<PersonalUserDetailDrawer open={!!activeUser} onClose={() => setActiveUser(null)} user={activeUser} />
		</>
	);
}
